"use client";

import { AlarmClock, ArrowDown, BellRing, CircleAlert, Link2, ListTodo } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

/* ------------------------------------------------------------------ */
/* Shared mock chrome                                                 */
/* ------------------------------------------------------------------ */

function MiniCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · A conversation becomes a linked task                           */
/* ------------------------------------------------------------------ */

function FromThreadMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <MiniCard className="p-2.5">
        <div className="flex items-start gap-2">
          <Avatar name="Casey Lin" src="/images/website/customers/casey-lin.jpg" size="xs" />
          <p className="min-w-0 flex-1 text-[12px] leading-snug text-foreground">
            &ldquo;Can you call me tomorrow about the 10-pack?&rdquo;
          </p>
        </div>
        <div className="mt-2 flex justify-end">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/50 px-2 py-1 text-[11px] font-medium text-foreground">
            <ListTodo className="size-3 text-muted-foreground" />
            Create task
          </span>
        </div>
      </MiniCard>

      <div className="flex justify-center">
        <ArrowDown className="size-3.5 text-muted-foreground" />
      </div>

      <MiniCard className="p-2.5">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 size-3.5 shrink-0 rounded-full border-[1.5px] border-border" />
          <div className="min-w-0">
            <p className="text-[12px] font-semibold leading-snug text-foreground">
              Call Casey re: membership options
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Link2 className="size-3" />
              Linked to Casey · View conversation
            </p>
          </div>
        </div>
      </MiniCard>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Owned, or waiting in the shared pool                           */
/* ------------------------------------------------------------------ */

function OwnershipMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <MiniCard className="p-2.5">
        <div className="flex items-center gap-2">
          <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-border" />
          <p className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">
            Call Maya re: intro offer
          </p>
          <Avatar name="Alex Romero" size="xs" />
        </div>
        <p className="mt-1.5 pl-[22px] text-[11px] text-muted-foreground">
          Alex owns it · due today 3:00 PM
        </p>
      </MiniCard>

      <MiniCard className="p-2.5">
        <div className="flex items-center gap-2">
          <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-border" />
          <p className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">
            Restock towels before 6am
          </p>
          <span className="size-5 shrink-0 rounded-full border border-dashed border-border" />
        </div>
        <p className="mt-1.5 pl-[22px] text-[11px] text-muted-foreground">
          Unassigned · anyone can claim it
        </p>
      </MiniCard>

      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] shadow-sm">
          <BellRing className="size-3 shrink-0 text-muted-foreground" />
          <span className="font-medium text-foreground">Push sent to Alex</span>
          <span className="text-muted-foreground">· web + iOS</span>
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · The nudges that keep it moving                                 */
/* ------------------------------------------------------------------ */

const NUDGES = [
  {
    icon: BellRing,
    title: "Task due soon",
    sub: "Call Casey re: 10-pack",
    time: "9:00 AM",
    alert: false,
  },
  {
    icon: CircleAlert,
    title: "Task overdue",
    sub: "Follow up on trial week",
    time: "5:12 PM",
    alert: true,
  },
  {
    icon: AlarmClock,
    title: "Snoozed task is back",
    sub: "Rebook Chris post-freeze",
    time: "3:00 PM",
    alert: false,
  },
];

function NudgesMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      {NUDGES.map(({ icon: Icon, title, sub, time, alert }) => (
        <MiniCard key={title} className="flex items-center gap-2.5 p-2.5">
          <span
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-lg border",
              alert ? "border-red-500/50 bg-red-500/10" : "border-border bg-muted/40",
            )}
          >
            <Icon
              className={cn(
                "size-3.5",
                alert ? "text-red-500 dark:text-red-400" : "text-muted-foreground",
              )}
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-foreground">{title}</p>
            <p className="truncate text-[11px] text-muted-foreground">{sub}</p>
          </div>
          <span className="shrink-0 text-[10px] text-muted-foreground">{time}</span>
        </MiniCard>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: <FromThreadMock />,
    title: "Born where the work comes up",
    body: "Create a task from a thread, the contact sidebar, or the board, or let a flow create it automatically. Each one stays linked to its contact and conversation, one tap from the full story.",
  },
  {
    visual: <OwnershipMock />,
    title: "Every task finds an owner",
    body: "Hand it to a teammate and they get a push on web and iOS, or leave it in the shared pool for whoever's free. Reassign anytime; the board always shows who's on what.",
  },
  {
    visual: <NudgesMock />,
    title: "Chased until it's checked off",
    body: "A push lands before the deadline, overdue tasks turn red and get counted, and snoozed tasks wake up with a ping at the time you picked. The chasing is built in.",
  },
];

export function TasksHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              From &ldquo;someone should&rdquo; to someone did.
              <span className="text-muted-foreground">
                {" "}
                Follow-ups, callbacks, and front-desk to-dos tracked in the same place as your
                conversations, not in a separate app, a group chat, or a sticky note.
              </span>
            </>
          }
        />
      </div>

      {/* Framed band: hatched side gutters, solid card body (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[188px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
